import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  X,
  SlidersHorizontal,
  LogOut,
  ListTodo,
  BarChart3,
  LayoutGrid,
  LayoutList,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/useSession";
import { TASK_DEPARTMENTS, TASK_MEMBERS, TASK_STATUSES, type TaskPriority, type TaskStatus } from "./tasksConstants";
import { TasksNewTaskForm, type NewTaskRow } from "./TasksNewTaskForm";
import { TasksTaskDetail } from "./TasksTaskDetail";
import {
  calendarDaysUntilDue,
  formatCreatorLabel,
  formatDeadlineBadge,
  formatDeadlineBadgeShort,
  formatTaskCode,
  isTaskDeadlineUrgent,
  parseTaskRouteParam,
  statusDotClass,
} from "./tasksUi";

type TaskRow = {
  id: string;
  task_number: number;
  title: string;
  assignees: string[];
  department: string;
  priority: TaskPriority;
  status: TaskStatus;
  created_at: string;
  updated_at: string;
  due_date: string | null;
  created_by_email: string | null;
  task_subtasks?: { is_done: boolean }[];
};

function normalize(s: string) {
  return s.trim().toLowerCase();
}

function taskSearchBlob(t: TaskRow): string {
  const hay = [
    t.title,
    t.department,
    t.status,
    ...(t.assignees ?? []),
    new Date(t.updated_at).toLocaleString(),
    formatTaskCode(t.task_number),
    String(t.task_number),
    t.created_by_email ?? "",
    t.due_date ?? "",
  ];
  if (typeof t.task_number === "number" && t.task_number <= 999) hay.push(String(t.task_number).padStart(3, "0"));
  return hay.join(" ").toLowerCase();
}

/** Full-string `T042` / `042` / UUID ⇒ exact task match only (no substring in titles). */
function matchesTaskSearch(t: TaskRow, rawQ: string): boolean {
  const trimmed = rawQ.trim();
  const qn = normalize(rawQ);

  const direct = parseTaskRouteParam(trimmed);
  if (direct?.by === "task_number") return t.task_number === direct.n;
  if (direct?.by === "id") return t.id === direct.id;

  if (!qn) return true;
  return taskSearchBlob(t).includes(qn);
}

type StatusFilter = TaskStatus | "all";

type ViewMode = "list" | "board";

type PriorityTriage = "all" | TaskPriority;

type DueTriage = "all" | "has_due" | "no_due" | "overdue" | "due_next_7";

type TaskSortKey = "updated_desc" | "due_asc" | "priority_high_first" | "department";

type BoardLane = "all" | "due" | "high";

function passesDueTriage(t: TaskRow, due: DueTriage): boolean {
  if (due === "all") return true;
  if (due === "has_due") return !!t.due_date;
  if (due === "no_due") return !t.due_date;
  const active = t.status !== "done" && t.status !== "cancelled";
  const days = calendarDaysUntilDue(t.due_date);
  if (due === "overdue") {
    if (!active) return false;
    return days !== null && days < 0;
  }
  if (due === "due_next_7") {
    if (!active) return false;
    return days !== null && days >= 0 && days <= 7;
  }
  return true;
}

function sortTasksForView(tasks: TaskRow[], sort: TaskSortKey): TaskRow[] {
  const copy = [...tasks];
  switch (sort) {
    case "due_asc":
      return copy.sort((a, b) => {
        const da = calendarDaysUntilDue(a.due_date);
        const db = calendarDaysUntilDue(b.due_date);
        const va = da === null ? 99999 : da;
        const vb = db === null ? 99999 : db;
        if (va !== vb) return va - vb;
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      });
    case "priority_high_first":
      return copy.sort((a, b) => {
        const pa = a.priority === "high" ? 0 : 1;
        const pb = b.priority === "high" ? 0 : 1;
        if (pa !== pb) return pa - pb;
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      });
    case "department":
      return copy.sort((a, b) => {
        const c = a.department.localeCompare(b.department);
        if (c !== 0) return c;
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      });
    default:
      return copy.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  }
}

type DeptTheme = { badge: string; chipBase: string; chipActive: string };

function departmentTheme(dept: string): DeptTheme {
  // Consistent palette: each theme uses the same intensity steps.
  switch (dept) {
    case "General":
      return {
        badge: "bg-slate-50 text-slate-700 ring-slate-200",
        chipBase: "bg-white text-slate-800 ring-slate-300",
        chipActive: "bg-slate-100 text-slate-900 ring-slate-500",
      };
    case "Finance":
      return {
        badge: "bg-amber-50 text-amber-800 ring-amber-200",
        chipBase: "bg-white text-amber-900 ring-amber-300",
        chipActive: "bg-amber-100 text-amber-950 ring-amber-500",
      };
    case "Sports":
      return {
        badge: "bg-sky-50 text-sky-800 ring-sky-200",
        chipBase: "bg-white text-sky-900 ring-sky-300",
        chipActive: "bg-sky-100 text-sky-950 ring-sky-500",
      };
    case "Education":
      return {
        badge: "bg-violet-50 text-violet-800 ring-violet-200",
        chipBase: "bg-white text-violet-900 ring-violet-300",
        chipActive: "bg-violet-100 text-violet-950 ring-violet-500",
      };
    case "Membership":
      return {
        badge: "bg-emerald-50 text-emerald-800 ring-emerald-200",
        chipBase: "bg-white text-emerald-900 ring-emerald-300",
        chipActive: "bg-emerald-100 text-emerald-950 ring-emerald-500",
      };
    case "Public Relations":
      return {
        badge: "bg-rose-50 text-rose-800 ring-rose-200",
        chipBase: "bg-white text-rose-900 ring-rose-300",
        chipActive: "bg-rose-100 text-rose-950 ring-rose-500",
      };
    case "Physical Resource":
      return {
        badge: "bg-teal-50 text-teal-800 ring-teal-200",
        chipBase: "bg-white text-teal-900 ring-teal-300",
        chipActive: "bg-teal-100 text-teal-950 ring-teal-500",
      };
    default:
      return {
        badge: "bg-brand-gray text-brand-blue/80 ring-black/10",
        chipBase: "bg-white text-brand-blue/80 ring-black/15",
        chipActive: "bg-brand-gray/40 text-brand-blue ring-brand-blue/30",
      };
  }
}

function departmentBadgeClass(dept: string) {
  return departmentTheme(dept).badge;
}

function departmentChipBaseClass(dept: string) {
  return departmentTheme(dept).chipBase;
}

function departmentChipActiveClass(dept: string) {
  return departmentTheme(dept).chipActive;
}

function useIsDesktopMd() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 768px)").matches : true
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const apply = () => setIsDesktop(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  return isDesktop;
}

export function TasksPage() {
  const navigate = useNavigate();
  const isDesktop = useIsDesktopMd();
  const { session } = useSession();
  const [addOpen, setAddOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tasks, setTasks] = useState<TaskRow[]>([]);

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === "undefined") return "list";
    try {
      const v = localStorage.getItem("oba_tasks_view");
      return v === "board" || v === "list" ? v : "list";
    } catch {
      return "list";
    }
  });
  const [priorityTriage, setPriorityTriage] = useState<PriorityTriage>("all");
  const [dueTriage, setDueTriage] = useState<DueTriage>("all");
  const [taskSort, setTaskSort] = useState<TaskSortKey>("updated_desc");
  const [boardLane, setBoardLane] = useState<BoardLane>("all");

  const [showDone, setShowDone] = useState(false);
  const [detailTaskId, setDetailTaskId] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem("oba_tasks_view", viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  useEffect(() => {
    if (viewMode !== "board" || !isDesktop || !detailTaskId) return;

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setDetailTaskId(null);
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewMode, isDesktop, detailTaskId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from("tasks")
        .select(
          "id,task_number,title,assignees,department,priority,status,created_at,updated_at,due_date,created_by_email,task_subtasks(is_done)"
        )
        .order("updated_at", { ascending: false });

      if (cancelled) return;

      if (error) {
        setError(error.message);
        setTasks([]);
      } else {
        setTasks((data ?? []) as TaskRow[]);
      }
      setLoading(false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!addOpen) return;

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setAddOpen(false);
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [addOpen]);

  useEffect(() => {
    if (!filtersOpen) return;

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setFiltersOpen(false);
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filtersOpen]);

  const filteredCore = useMemo(() => {
    return tasks.filter((t) => {
      if (departmentFilter !== "all" && normalize(t.department) !== normalize(departmentFilter)) return false;
      if (assigneeFilter === "unassigned") {
        if ((t.assignees?.length ?? 0) > 0) return false;
      } else if (assigneeFilter !== "all" && !t.assignees.includes(assigneeFilter)) {
        return false;
      }
      return matchesTaskSearch(t, q);
    });
  }, [tasks, q, departmentFilter, assigneeFilter]);

  const filteredForList = useMemo(() => {
    return filteredCore.filter((t) => {
      if (priorityTriage === "high" && t.priority !== "high") return false;
      if (priorityTriage === "normal" && t.priority !== "normal") return false;
      if (!passesDueTriage(t, dueTriage)) return false;
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      return true;
    });
  }, [filteredCore, statusFilter, priorityTriage, dueTriage]);

  const filteredForBoard = useMemo(() => {
    return filteredCore.filter((t) => {
      if (boardLane === "high") return t.priority === "high";
      if (boardLane === "due") return !!t.due_date;
      return true;
    });
  }, [filteredCore, boardLane]);

  // For department summary counts, we intentionally ignore the department filter so the numbers
  // remain stable while switching departments (counts still respect search/status/assignee).
  const filteredNoDept = useMemo(() => {
    return tasks.filter((t) => {
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (assigneeFilter === "unassigned") {
        if ((t.assignees?.length ?? 0) > 0) return false;
      } else if (assigneeFilter !== "all" && !t.assignees.includes(assigneeFilter)) {
        return false;
      }

      return matchesTaskSearch(t, q);
    });
  }, [tasks, q, statusFilter, assigneeFilter]);

  const notDone = useMemo(() => filteredForList.filter((t) => t.status !== "done"), [filteredForList]);
  const done = useMemo(
    () => sortTasksForView(filteredForList.filter((t) => t.status === "done"), taskSort),
    [filteredForList, taskSort]
  );
  const notDoneNoDept = useMemo(() => filteredNoDept.filter((t) => t.status !== "done"), [filteredNoDept]);

  const deadlineUrgentNotDone = useMemo(() => {
    const list = notDone.filter((t) => isTaskDeadlineUrgent(t.due_date, t.status, 5));
    return sortTasksForView(list, taskSort);
  }, [notDone, taskSort]);

  const deadlineUrgentIds = useMemo(
    () => new Set(deadlineUrgentNotDone.map((t) => t.id)),
    [deadlineUrgentNotDone]
  );

  const highNotDone = useMemo(
    () =>
      sortTasksForView(
        notDone.filter((t) => t.priority === "high" && !deadlineUrgentIds.has(t.id)),
        taskSort
      ),
    [notDone, deadlineUrgentIds, taskSort]
  );
  const normalNotDone = useMemo(
    () =>
      sortTasksForView(
        notDone.filter((t) => t.priority !== "high" && !deadlineUrgentIds.has(t.id)),
        taskSort
      ),
    [notDone, deadlineUrgentIds, taskSort]
  );

  const boardTodo = useMemo(
    () => sortTasksForView(filteredForBoard.filter((t) => t.status === "to-do"), taskSort),
    [filteredForBoard, taskSort]
  );
  const boardProgress = useMemo(
    () => sortTasksForView(filteredForBoard.filter((t) => t.status === "progress"), taskSort),
    [filteredForBoard, taskSort]
  );
  const boardCancelled = useMemo(
    () => sortTasksForView(filteredForBoard.filter((t) => t.status === "cancelled"), taskSort),
    [filteredForBoard, taskSort]
  );
  const boardDone = useMemo(
    () => sortTasksForView(filteredForBoard.filter((t) => t.status === "done"), taskSort),
    [filteredForBoard, taskSort]
  );
  const filtersActive =
    statusFilter !== "all" ||
    departmentFilter !== "all" ||
    assigneeFilter !== "all" ||
    (viewMode === "list" && (priorityTriage !== "all" || dueTriage !== "all" || taskSort !== "updated_desc")) ||
    (viewMode === "board" && boardLane !== "all");

  const deptCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of TASK_DEPARTMENTS) counts.set(d, 0);
    for (const t of notDoneNoDept) {
      const key = TASK_DEPARTMENTS.find((d) => normalize(d) === normalize(t.department)) ?? t.department;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return TASK_DEPARTMENTS.map((d) => ({ department: d, count: counts.get(d) ?? 0 }));
  }, [notDoneNoDept]);

  function openTask(t: TaskRow) {
    if (viewMode === "board") {
      setDetailTaskId(t.id);
      if (!isDesktop) void navigate(`/tasks/${formatTaskCode(t.task_number)}`);
      return;
    }
    if (isDesktop) setDetailTaskId(t.id);
    else void navigate(`/tasks/${formatTaskCode(t.task_number)}`);
  }

  const boardVisibleCount = boardTodo.length + boardProgress.length + boardDone.length;

  function renderBoardColumn(
    title: string,
    list: TaskRow[],
    shellClass: string,
    headerClass: string
  ) {
    return (
      <section
        className={[
          "flex min-h-0 min-w-[min(100%,17rem)] flex-1 flex-col rounded-2xl p-2 ring-1 md:min-w-0",
          shellClass,
        ].join(" ")}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 px-2 py-1.5">
          <p className={["text-xs font-bold uppercase tracking-wide", headerClass].join(" ")}>{title}</p>
          <span className="rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-bold text-brand-blue/70 ring-1 ring-black/10">
            {list.length}
          </span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-1 pb-1 [scrollbar-color:rgba(15,23,42,0.35)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin] [-ms-overflow-style:auto]">
          <div className="flex flex-col gap-2 pr-0.5">
            {list.length === 0 ? (
              <p className="px-2 py-6 text-center text-[11px] font-semibold text-brand-blue/45">No tasks</p>
            ) : (
              list.map((t) => (
                <div
                  key={t.id}
                  className="overflow-hidden rounded-xl border border-black/10 bg-white shadow-sm ring-1 ring-black/[0.04]"
                >
                  <TaskListRow
                    variant="board"
                    task={t}
                    isActive={detailTaskId === t.id}
                    onOpen={() => openTask(t)}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="min-h-screen bg-brand-gray text-brand-blue">
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div>
          <div className="flex items-center justify-between gap-3">
            <p className="font-display text-2xl font-bold">OBA Tasks</p>

            <div className="flex flex-nowrap items-center justify-end gap-2">
            {session?.user?.email ? (
              <div className="hidden max-w-[16rem] truncate rounded-full bg-white px-3 py-2 text-sm font-semibold text-brand-blue/80 shadow-sm ring-1 ring-black/10 md:block">
                {session.user.email}
              </div>
            ) : null}

            <Link
              to="/agenda"
              title="Meeting topic votes"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-brand-blue shadow-sm ring-1 ring-black/10 hover:bg-brand-gray/20 md:inline-flex"
              aria-label="Meeting topic votes"
            >
              <ListTodo className="h-5 w-5" />
            </Link>
            <Link
              to="/tasks/dashboard"
              title="Tasks dashboard"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-brand-blue shadow-sm ring-1 ring-black/10 hover:bg-brand-gray/20 md:inline-flex"
              aria-label="Tasks dashboard"
            >
              <BarChart3 className="h-5 w-5" />
            </Link>

            <button
              type="button"
              title="Filters"
              aria-label="Filters"
              onClick={() => setFiltersOpen(true)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-blue text-brand-gray shadow-sm ring-1 ring-black/5 hover:opacity-95 md:hidden"
            >
              <SlidersHorizontal className="h-5 w-5" />
            </button>
            <button
              type="button"
              title="Add new item"
              aria-label="Add new item"
              onClick={() => {
                if (isDesktop) setAddOpen(true);
                else navigate("/tasks/new");
              }}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-maroon text-white shadow-sm ring-1 ring-black/5 hover:opacity-95"
            >
              <Plus className="h-5 w-5" />
            </button>
            <button
              type="button"
              title="Log out"
              aria-label="Log out"
              onClick={() => void supabase.auth.signOut()}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-brand-blue shadow-sm ring-1 ring-black/10 hover:bg-brand-gray/20"
            >
              <LogOut className="h-5 w-5" />
            </button>
            {filtersActive ? (
              <button
                type="button"
                onClick={() => {
                  setQ("");
                  setStatusFilter("all");
                  setDepartmentFilter("all");
                  setAssigneeFilter("all");
                  setPriorityTriage("all");
                  setDueTriage("all");
                  setTaskSort("updated_desc");
                  setBoardLane("all");
                }}
                className="hidden h-10 w-10 items-center justify-center rounded-full bg-white text-brand-blue shadow-sm ring-1 ring-black/10 hover:bg-brand-gray/20 md:inline-flex"
                title="Clear filters"
                aria-label="Clear filters"
              >
                <X className="h-5 w-5" />
              </button>
            ) : null}
            </div>
          </div>
        </div>

        {addOpen ? (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 p-4 md:items-center">
            <button
              type="button"
              aria-label="Close"
              className="absolute inset-0 cursor-default"
              onClick={() => setAddOpen(false)}
            />
            <div
              role="dialog"
              aria-modal="true"
              className="relative z-[101] w-full max-w-3xl rounded-2xl bg-white p-5 shadow-xl ring-1 ring-black/10"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display text-xl font-bold">Add new item</p>
                  <p className="mt-1 text-sm text-brand-blue/70">Create a task, then it appears at the top of the list.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setAddOpen(false)}
                  className="rounded-full p-2 text-brand-blue/70 hover:bg-brand-gray/40"
                  aria-label="Close dialog"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-5">
                <TasksNewTaskForm
                  submitLabel="Add task"
                  onSaved={(row: NewTaskRow) => {
                    setTasks((cur) => [row as TaskRow, ...cur]);
                    setAddOpen(false);
                  }}
                  onCancel={() => setAddOpen(false)}
                />
              </div>
            </div>
          </div>
        ) : null}

        {/* List + desktop: right-side details pane. Board + desktop: modal. Mobile: /tasks/:id. */}

        <div className="mt-6 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-black/5 md:px-4 md:py-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="hidden flex-wrap items-center gap-2 md:flex">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              disabled={viewMode === "board"}
              title={viewMode === "board" ? "Board view uses columns for status" : undefined}
              className="rounded-xl bg-brand-gray/40 px-3 py-2 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="all">All statuses</option>
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="rounded-xl bg-brand-gray/40 px-3 py-2 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
            >
              <option value="all">All departments</option>
              {TASK_DEPARTMENTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>

            <select
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className="rounded-xl bg-brand-gray/40 px-3 py-2 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
            >
              <option value="all">All assignees</option>
                    <option value="unassigned">Unassigned</option>
              {TASK_MEMBERS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>

            {filtersActive ? (
              <span className="ml-1 rounded-full bg-brand-gray px-2 py-0.5 text-[11px] font-bold text-brand-blue/70 ring-1 ring-black/10">
                Active
              </span>
            ) : null}
            </div>

            <div className="relative w-full md:max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-blue/50" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tasks, title, assignee… (e.g. T042 or 042)"
              className="w-full rounded-xl bg-brand-gray/40 py-2.5 pl-10 pr-10 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
            />
            {q.trim() ? (
              <button
                type="button"
                onClick={() => setQ("")}
                className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-brand-blue/60 hover:bg-brand-gray/40"
                aria-label="Clear search"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
            </div>
          </div>
        </div>

        {/* Summary (mobile-first): Department counts exclude Done */}
        <div className="mt-3 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-black/5 md:p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-bold uppercase tracking-wide text-brand-blue/60">Summary</p>
            <p className="text-[11px] font-semibold text-brand-blue/50">Active tasks</p>
          </div>

          <div className="mt-3 space-y-3">
            <div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setDepartmentFilter("all")}
                  className={[
                    "shrink-0 rounded-full px-4 py-2 text-sm font-bold ring-1 transition-colors",
                    departmentFilter === "all"
                      ? "bg-brand-blue/10 text-brand-blue ring-brand-blue/40 ring-2 shadow-sm"
                      : "bg-white text-brand-blue/80 ring-black/15 hover:bg-brand-gray/20",
                  ].join(" ")}
                  title="All categories"
                >
                  All · {notDoneNoDept.length}
                </button>
                {deptCounts.map((d) => (
                  <button
                    key={d.department}
                    type="button"
                    onClick={() => setDepartmentFilter(d.department)}
                    className={[
                      "shrink-0 rounded-full px-4 py-2 text-sm font-bold ring-1 transition-colors hover:bg-brand-gray/15",
                      departmentFilter === d.department
                        ? ["ring-2 shadow-sm", departmentChipActiveClass(d.department)].join(" ")
                        : departmentChipBaseClass(d.department),
                    ].join(" ")}
                    title={d.department}
                  >
                    {d.department} · {d.count}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* View switch below summary; list uses Due/Priority/Sort, board uses All / Due / High */}
        <div className="mt-3 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-black/5 md:p-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wide text-brand-blue/50">View</span>
              <div className="inline-flex rounded-full bg-brand-gray/40 p-1 ring-1 ring-black/10">
                <button
                  type="button"
                  aria-pressed={viewMode === "list"}
                  onClick={() => setViewMode("list")}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors",
                    viewMode === "list"
                      ? "bg-white text-brand-blue shadow-sm ring-1 ring-black/10"
                      : "text-brand-blue/70 hover:text-brand-blue",
                  ].join(" ")}
                >
                  <LayoutList className="h-4 w-4" />
                  List
                </button>
                <button
                  type="button"
                  aria-pressed={viewMode === "board"}
                  onClick={() => setViewMode("board")}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors",
                    viewMode === "board"
                      ? "bg-white text-brand-blue shadow-sm ring-1 ring-black/10"
                      : "text-brand-blue/70 hover:text-brand-blue",
                  ].join(" ")}
                >
                  <LayoutGrid className="h-4 w-4" />
                  Board
                </button>
              </div>
            </div>

            {viewMode === "list" ? (
              <div className="flex flex-col gap-2 border-t border-black/5 pt-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-brand-blue/50">Narrow list</p>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={dueTriage}
                    onChange={(e) => setDueTriage(e.target.value as DueTriage)}
                    className="min-w-[10rem] flex-1 rounded-xl bg-brand-gray/40 px-3 py-2 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon sm:flex-none"
                  >
                    <option value="all">Due: Any</option>
                    <option value="has_due">Due: Has deadline</option>
                    <option value="no_due">Due: No deadline</option>
                    <option value="overdue">Due: Overdue</option>
                    <option value="due_next_7">Due: Next 7 days</option>
                  </select>

                  <select
                    value={priorityTriage}
                    onChange={(e) => setPriorityTriage(e.target.value as PriorityTriage)}
                    className="min-w-[9rem] flex-1 rounded-xl bg-brand-gray/40 px-3 py-2 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon sm:flex-none"
                  >
                    <option value="all">Priority: Any</option>
                    <option value="high">Priority: High</option>
                    <option value="normal">Priority: Normal</option>
                  </select>

                  <select
                    value={taskSort}
                    onChange={(e) => setTaskSort(e.target.value as TaskSortKey)}
                    className="min-w-[11rem] flex-1 rounded-xl bg-brand-gray/40 px-3 py-2 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon sm:flex-none"
                  >
                    <option value="updated_desc">Sort: Recently updated</option>
                    <option value="due_asc">Sort: Due date (soonest)</option>
                    <option value="priority_high_first">Sort: High priority first</option>
                    <option value="department">Sort: Department</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2 border-t border-black/5 pt-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-brand-blue/50">Show on board</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    aria-pressed={boardLane === "all"}
                    onClick={() => setBoardLane("all")}
                    className={[
                      "rounded-full px-4 py-2 text-xs font-bold ring-1 transition-colors",
                      boardLane === "all"
                        ? "bg-brand-blue/10 text-brand-blue ring-brand-blue/40 ring-2 shadow-sm"
                        : "bg-white text-brand-blue/80 ring-black/15 hover:bg-brand-gray/20",
                    ].join(" ")}
                  >
                    All tasks
                  </button>
                  <button
                    type="button"
                    aria-pressed={boardLane === "due"}
                    onClick={() => setBoardLane("due")}
                    className={[
                      "rounded-full px-4 py-2 text-xs font-bold ring-1 transition-colors",
                      boardLane === "due"
                        ? "bg-sky-100 text-sky-950 ring-sky-400 ring-2 shadow-sm"
                        : "bg-white text-brand-blue/80 ring-black/15 hover:bg-brand-gray/20",
                    ].join(" ")}
                  >
                    Due
                  </button>
                  <button
                    type="button"
                    aria-pressed={boardLane === "high"}
                    onClick={() => setBoardLane("high")}
                    className={[
                      "rounded-full px-4 py-2 text-xs font-bold ring-1 transition-colors",
                      boardLane === "high"
                        ? "bg-amber-100 text-amber-950 ring-amber-400 ring-2 shadow-sm"
                        : "bg-white text-brand-blue/80 ring-black/15 hover:bg-brand-gray/20",
                    ].join(" ")}
                  >
                    High priority
                  </button>
                </div>
                <p className="text-[11px] leading-snug text-brand-blue/50">
                  Columns still reflect status (To do / Progress / Done). This only limits which tasks appear.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Mobile: filters as popup */}
        {filtersOpen && !isDesktop ? (
          <div className="fixed inset-0 z-[120] flex items-end justify-center bg-black/40 p-4 md:hidden">
            <button
              type="button"
              aria-label="Close filters"
              className="absolute inset-0 cursor-default"
              onClick={() => setFiltersOpen(false)}
            />
            <div className="relative z-[121] w-full max-w-md rounded-2xl bg-white p-4 shadow-xl ring-1 ring-black/10">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display text-lg font-bold text-brand-blue">Filters</p>
                  <p className="mt-1 text-sm text-brand-blue/70">Narrow the list.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFiltersOpen(false)}
                  className="rounded-full p-2 text-brand-blue/70 hover:bg-brand-gray/40"
                  aria-label="Close filters"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-4 space-y-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                  disabled={viewMode === "board"}
                  className="w-full rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon disabled:opacity-50"
                >
                  <option value="all">All statuses</option>
                  {TASK_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>

                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="w-full rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
                >
                  <option value="all">All departments</option>
                  {TASK_DEPARTMENTS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>

                <select
                  value={assigneeFilter}
                  onChange={(e) => setAssigneeFilter(e.target.value)}
                  className="w-full rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
                >
                  <option value="all">All assignees</option>
                    <option value="unassigned">Unassigned</option>
                  {TASK_MEMBERS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>

                {viewMode === "list" ? (
                  <>
                    <select
                      value={dueTriage}
                      onChange={(e) => setDueTriage(e.target.value as DueTriage)}
                      className="w-full rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
                    >
                      <option value="all">Due: Any</option>
                      <option value="has_due">Due: Has deadline</option>
                      <option value="no_due">Due: No deadline</option>
                      <option value="overdue">Due: Overdue</option>
                      <option value="due_next_7">Due: Next 7 days</option>
                    </select>

                    <select
                      value={priorityTriage}
                      onChange={(e) => setPriorityTriage(e.target.value as PriorityTriage)}
                      className="w-full rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
                    >
                      <option value="all">Priority: Any</option>
                      <option value="high">Priority: High</option>
                      <option value="normal">Priority: Normal</option>
                    </select>

                    <select
                      value={taskSort}
                      onChange={(e) => setTaskSort(e.target.value as TaskSortKey)}
                      className="w-full rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
                    >
                      <option value="updated_desc">Sort: Recently updated</option>
                      <option value="due_asc">Sort: Due date (soonest)</option>
                      <option value="priority_high_first">Sort: High priority first</option>
                      <option value="department">Sort: Department</option>
                    </select>
                  </>
                ) : (
                  <div>
                    <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-brand-blue/50">Show on board</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className={[
                          "flex-1 rounded-full px-3 py-2.5 text-xs font-bold ring-1 transition-colors min-w-[5.5rem]",
                          boardLane === "all"
                            ? "bg-brand-blue/10 text-brand-blue ring-brand-blue/40 ring-2"
                            : "bg-brand-gray/30 text-brand-blue/80 ring-black/10",
                        ].join(" ")}
                        onClick={() => setBoardLane("all")}
                      >
                        All
                      </button>
                      <button
                        type="button"
                        className={[
                          "flex-1 rounded-full px-3 py-2.5 text-xs font-bold ring-1 transition-colors min-w-[5.5rem]",
                          boardLane === "due"
                            ? "bg-sky-100 text-sky-950 ring-sky-400 ring-2"
                            : "bg-brand-gray/30 text-brand-blue/80 ring-black/10",
                        ].join(" ")}
                        onClick={() => setBoardLane("due")}
                      >
                        Due
                      </button>
                      <button
                        type="button"
                        className={[
                          "flex-1 rounded-full px-3 py-2.5 text-xs font-bold ring-1 transition-colors min-w-[5.5rem]",
                          boardLane === "high"
                            ? "bg-amber-100 text-amber-950 ring-amber-400 ring-2"
                            : "bg-brand-gray/30 text-brand-blue/80 ring-black/10",
                        ].join(" ")}
                        onClick={() => setBoardLane("high")}
                      >
                        High
                      </button>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setFiltersOpen(false)}
                  className="mt-1 inline-flex w-full items-center justify-center rounded-full bg-brand-blue px-6 py-3 text-sm font-bold text-brand-gray"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div
          className={[
            "mt-6 grid gap-4",
            viewMode === "list" ? "md:grid-cols-[minmax(0,1fr)_minmax(0,420px)] md:items-start" : "",
          ].join(" ")}
        >
          <div className="min-w-0">
            {loading ? (
              <div className="rounded-2xl bg-white p-8 text-center text-sm text-brand-blue/70 shadow-sm ring-1 ring-black/5">
                Loading…
              </div>
            ) : viewMode === "board" ? (
              <div className="flex max-h-[min(72vh,calc(100vh-11.5rem))] min-h-[min(22rem,48vh)] flex-col overflow-hidden rounded-2xl bg-white p-3 shadow-sm ring-1 ring-black/5 md:max-h-[calc(100vh-9.5rem)] md:min-h-[min(26rem,52vh)] md:p-4">
                {boardVisibleCount === 0 && boardCancelled.length === 0 ? (
                  <div className="px-4 py-10 text-center text-sm text-brand-blue/70">No matching tasks.</div>
                ) : (
                  <>
                    <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] md:grid md:grid-cols-3 md:gap-4 md:overflow-hidden md:pb-0 md:[scrollbar-width:auto]">
                      {renderBoardColumn(
                        "To do",
                        boardTodo,
                        "bg-slate-50/90 ring-slate-200/90",
                        "text-slate-800"
                      )}
                      {renderBoardColumn(
                        "Progress",
                        boardProgress,
                        "bg-sky-50/90 ring-sky-200/90",
                        "text-sky-950"
                      )}
                      {renderBoardColumn("Done", boardDone, "bg-emerald-50/90 ring-emerald-200/90", "text-emerald-950")}
                    </div>
                    {boardCancelled.length ? (
                      <div className="mt-3 rounded-xl border border-dashed border-brand-blue/20 bg-brand-gray/20 px-3 py-2 text-xs font-semibold text-brand-blue/70">
                        {boardCancelled.length} cancelled task{boardCancelled.length === 1 ? "" : "s"} match your filters — switch to{" "}
                        <button
                          type="button"
                          className="font-bold text-brand-maroon underline decoration-brand-maroon/40"
                          onClick={() => {
                            setViewMode("list");
                            setStatusFilter("cancelled");
                          }}
                        >
                          List
                        </button>{" "}
                        and filter by Cancelled to review.
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : (
              <div className="rounded-2xl bg-white shadow-sm ring-1 ring-black/5">
                <div className="divide-y divide-black/5">
                  {notDone.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-brand-blue/70">
                      No matching tasks.
                    </div>
                  ) : null}

                  {deadlineUrgentNotDone.length ? (
                    <div className="bg-amber-50/90 px-4 py-2 text-xs font-bold uppercase tracking-wide text-amber-950/80 ring-1 ring-amber-200/80">
                      Due within 5 days · Overdue
                    </div>
                  ) : null}

                  {deadlineUrgentNotDone.map((t) => (
                    <TaskListRow
                      key={t.id}
                      task={t}
                      isActive={isDesktop && detailTaskId === t.id}
                      onOpen={() => openTask(t)}
                    />
                  ))}

                  {highNotDone.length ? (
                    <div className="bg-brand-gray/20 px-4 py-2 text-xs font-bold uppercase tracking-wide text-brand-blue/60">
                      Priority High
                    </div>
                  ) : null}

                  {highNotDone.map((t) => (
                    <TaskListRow
                      key={t.id}
                      task={t}
                      isActive={isDesktop && detailTaskId === t.id}
                      onOpen={() => openTask(t)}
                    />
                  ))}

                  {highNotDone.length && normalNotDone.length ? (
                    <div className="bg-brand-gray/20 px-4 py-2 text-xs font-bold uppercase tracking-wide text-brand-blue/60">
                      All tasks
                    </div>
                  ) : null}

                  {deadlineUrgentNotDone.length && normalNotDone.length && highNotDone.length === 0 ? (
                    <div className="bg-brand-gray/20 px-4 py-2 text-xs font-bold uppercase tracking-wide text-brand-blue/60">
                      All tasks
                    </div>
                  ) : null}

                  {normalNotDone.map((t) => (
                    <TaskListRow
                      key={t.id}
                      task={t}
                      isActive={isDesktop && detailTaskId === t.id}
                      onOpen={() => openTask(t)}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => setShowDone((v) => !v)}
                  className="flex w-full items-center justify-between gap-2 border-t border-black/5 px-4 py-3 text-sm font-semibold text-brand-blue/80"
                >
                  <span>Done ({done.length})</span>
                  {showDone ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>

                {showDone ? (
                  <div className="divide-y divide-black/5">
                    {done.map((t) => (
                      <TaskListRow
                        key={t.id}
                        task={t}
                        isActive={isDesktop && detailTaskId === t.id}
                        onOpen={() => openTask(t)}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <aside
            className={[
              "sticky top-4 hidden max-h-[calc(100vh-2rem)]",
              viewMode === "list" ? "md:block" : "md:hidden",
            ].join(" ")}
          >
            <div className="flex max-h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-black/5">
              {detailTaskId ? (
                <>
                  <div className="flex shrink-0 items-start justify-between gap-3 border-b border-black/5 px-4 py-3">
                    <div>
                      <p className="font-display text-lg font-bold">Details</p>
                      <p className="mt-0.5 text-xs text-brand-blue/70">Edit fields and checklist.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDetailTaskId(null)}
                      className="rounded-full p-2 text-brand-blue/70 hover:bg-brand-gray/40"
                      aria-label="Close details"
                      title="Close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                    <TasksTaskDetail
                      taskId={detailTaskId}
                      onClose={() => setDetailTaskId(null)}
                      onDeleted={() => {
                        setTasks((cur) => cur.filter((t) => t.id !== detailTaskId));
                        setDetailTaskId(null);
                      }}
                      onUpdated={(updated) => {
                        setTasks((cur) => cur.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
                      }}
                    />
                  </div>
                </>
              ) : (
                <div className="m-4 rounded-xl bg-brand-gray/20 p-6 text-sm text-brand-blue/70">
                  Select a task to see details.
                </div>
              )}
            </div>
          </aside>
        </div>

        {viewMode === "board" && isDesktop && detailTaskId ? (
          <div className="fixed inset-0 z-[130] flex items-end justify-center bg-black/45 p-0 sm:p-4 md:items-center">
            <button
              type="button"
              aria-label="Close task details"
              className="absolute inset-0 cursor-default"
              onClick={() => setDetailTaskId(null)}
            />
            <div
              role="dialog"
              aria-modal="true"
              className="relative z-[131] flex max-h-[min(92vh,calc(100vh-2rem))] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl ring-1 ring-black/10 sm:max-h-[90vh] sm:rounded-2xl"
            >
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-black/5 px-4 py-3">
                <div>
                  <p className="font-display text-lg font-bold">Task</p>
                  <p className="mt-0.5 text-xs text-brand-blue/70">Edit fields and checklist.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setDetailTaskId(null)}
                  className="rounded-full p-2 text-brand-blue/70 hover:bg-brand-gray/40"
                  aria-label="Close"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                <TasksTaskDetail
                  taskId={detailTaskId}
                  onClose={() => setDetailTaskId(null)}
                  onDeleted={() => {
                    setTasks((cur) => cur.filter((t) => t.id !== detailTaskId));
                    setDetailTaskId(null);
                  }}
                  onUpdated={(updated) => {
                    setTasks((cur) => cur.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
                  }}
                />
              </div>
            </div>
          </div>
        ) : null}

        {error ? (
          <p className="mt-6 rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 shadow-sm ring-1 ring-red-700/10">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function TaskListRow(props: {
  task: TaskRow;
  onOpen: () => void;
  isActive?: boolean;
  variant?: "list" | "board";
}) {
  const t = props.task;
  const variant = props.variant ?? "list";
  const code = typeof t.task_number === "number" ? formatTaskCode(t.task_number) : "…";
  const isDone = t.status === "done";
  const isCancelled = t.status === "cancelled";
  const assigned =
    (t.assignees?.length ?? 0) === 0
      ? "—"
      : t.assignees.length <= 2
        ? t.assignees.join(", ")
        : `${t.assignees[0]}, +${t.assignees.length - 1}`;

  const creator = formatCreatorLabel(t.created_by_email);
  const dueDays = !isDone && !isCancelled ? calendarDaysUntilDue(t.due_date) : null;

  const totalSubtasks = t.task_subtasks?.length ?? 0;
  const doneSubtasks = t.task_subtasks?.filter((s) => s.is_done).length ?? 0;
  const subtaskBadge = totalSubtasks ? `${doneSubtasks}/${totalSubtasks}` : null;

  const deadlinePill =
    dueDays !== null
      ? {
          label: formatDeadlineBadgeShort(dueDays),
          className:
            dueDays < 0
              ? "bg-rose-50 text-rose-900 ring-rose-200/90"
              : dueDays === 0
                ? "bg-amber-50 text-amber-950 ring-amber-200/90"
                : "bg-sky-50 text-sky-950 ring-sky-200/80",
        }
      : null;

  return (
    <button
      type="button"
      onClick={props.onOpen}
      className={[
        "w-full text-left transition-colors hover:bg-brand-gray/25 focus:outline-none focus:ring-2 focus:ring-brand-maroon/40",
        variant === "board" ? "px-2.5 py-2 sm:px-3 sm:py-2.5" : "px-3 py-3.5 sm:px-4 md:py-3 md:px-4",
        props.isActive
          ? "bg-brand-maroon/10 shadow-sm ring-2 ring-brand-maroon/30 ring-inset"
          : "",
        isDone ? "opacity-80" : "",
      ].join(" ")}
    >
      <div className={["flex items-start justify-between gap-2 sm:gap-4", variant === "board" ? "sm:gap-3" : ""].join(" ")}>
        <div className="flex min-w-0 flex-1 items-start gap-1.5 sm:gap-2.5">
          <span
            className={[
              "shrink-0 font-mono text-[11px] font-bold tracking-tight tabular-nums text-brand-blue/45",
              variant === "board" ? "pt-px" : "pt-0.5 md:pt-[1px]",
              props.isActive ? "!text-brand-maroon" : "",
            ].join(" ")}
          >
            {code}
          </span>
          <div className="min-w-0 flex-1">
            <p
              className={[
                "font-bold leading-snug text-brand-blue",
                variant === "board"
                  ? "line-clamp-2 text-sm leading-snug"
                  : "line-clamp-2 text-base sm:text-lg md:text-base",
                isDone ? "line-through decoration-brand-blue/30" : "",
                isCancelled ? "text-brand-blue/60" : "",
                props.isActive ? "text-brand-maroon" : "",
              ].join(" ")}
            >
              {t.title}
            </p>
            <p
              className={[
                "truncate leading-tight text-brand-blue/65",
                variant === "board" ? "mt-0.5 text-[11px]" : "mt-1 text-sm md:text-xs",
              ].join(" ")}
            >
              {assigned}
            </p>
            {creator && variant === "list" ? (
              <p className="mt-0.5 truncate text-[11px] font-semibold leading-tight text-brand-blue/45 md:text-[10px]">
                Created by {creator}
              </p>
            ) : null}
          </div>
        </div>

        <div
          className={[
            "flex shrink-0 flex-wrap items-center justify-end gap-1 sm:gap-2 md:gap-3",
            variant === "board" ? "max-w-[48%] flex-col items-end gap-1" : "max-w-[44%] sm:max-w-none",
          ].join(" ")}
        >
          <div className="flex flex-wrap items-center justify-end gap-1 sm:gap-1.5 md:gap-2">
            {subtaskBadge ? (
              <span className="rounded-full bg-brand-gray px-2 py-0.5 text-[10px] font-bold text-brand-blue/70 ring-1 ring-black/10 sm:text-[11px]">
                {subtaskBadge}
              </span>
            ) : null}
            {deadlinePill && dueDays !== null ? (
              <span
                className={[
                  "max-w-[7.5rem] truncate rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 sm:max-w-none sm:text-[11px]",
                  deadlinePill.className,
                ].join(" ")}
                title={formatDeadlineBadge(dueDays)}
              >
                {deadlinePill.label}
              </span>
            ) : null}
            <span
              className={[
                "rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 sm:text-[11px]",
                variant === "board" ? "inline-flex max-w-[9rem] truncate" : "hidden md:inline-flex",
                departmentBadgeClass(t.department),
              ].join(" ")}
            >
              {t.department}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span
              className={[
                "h-3 w-3 shrink-0 rounded-full ring-2 ring-white/80",
                variant === "board" ? "" : "mt-0.5",
                statusDotClass(t.status),
              ].join(" ")}
              aria-hidden="true"
              title={t.status}
            />
            {variant === "list" && props.isActive ? (
              <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-brand-maroon" />
            ) : (
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue/40" />
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

